/* eslint-disable react/no-danger */
import { FC } from 'react';

import { IconBlock, IconBlockInfo } from 'app/components/ui/icon-block';
import { TextWithLinks } from 'app/components/ui/text-with-links';
import { useRichDescriptionStyles } from 'app/components/content-details/use-rich-description-styles';
import { useTrackDescriptionLinkClick } from 'app/components/content-details/use-track-description-link-click';

export enum RichDescriptionType {
  BASIC = 'BASIC',
  CUSTOM = 'CUSTOM',
}

export interface ContentDetailsProps {
  title: string;
  upperSubtitle?: string;
  lowerSubtitle?: string;
  description?: string | undefined;
  richDescription?: string;
  richDescriptionType?: RichDescriptionType;
  showRichDescription?: boolean;
  descriptionTitleText?: string;
  haveClaimedText?: string;
  descriptionViewDetailText: string;
  iconBlocks?: Array<IconBlockInfo>;
  participantsCount?: string;
  hostedBy?: string;
}

export const ContentDetails: FC<ContentDetailsProps> = ({
  title,
  upperSubtitle,
  lowerSubtitle,
  description,
  richDescription,
  richDescriptionType: _richDescriptionType = RichDescriptionType.BASIC,
  showRichDescription,
  iconBlocks,
  descriptionTitleText,
  descriptionViewDetailText,
  participantsCount,
  haveClaimedText,
  hostedBy,
}: ContentDetailsProps) => {
  const trackDescriptionLinkClick = useTrackDescriptionLinkClick();
  const { richDescriptionRef } = useRichDescriptionStyles({
    richDescription,
    showRichDescription,
    onLinkClick: trackDescriptionLinkClick,
  });

  return (
    <div className="w-full md:max-w-[80%] bg-white">
      {/* UpperSubtitle - matches styled-components UpperSubtitle */}
      {upperSubtitle && (
        <div className="uppercase font-medium text-xs sm:text-sm leading-5 sm:leading-[22px] mb-3 sm:mb-4 text-primary">
          {upperSubtitle}
        </div>
      )}

      {/* PageTitleContainer - matches styled-components PageTitleContainer */}
      <h1 className="mb-1 md:mb-3 font-bold text-3xl lg:text-3xl xl:text-4xl sm:leading-7 lg:leading-8 xl:leading-9">
        {title}
      </h1>

      {/* LowerSubtitle - matches styled-components LowerSubtitle */}
      {lowerSubtitle && (
        <div className="font-normal text-lg lg:text-xl xl:text-2xl leading-6 sm:leading-7 mt-2 sm:mt-3 mb-3 sm:mb-4 text-gray-500">
          {lowerSubtitle}
        </div>
      )}

      {/* HostedBy - matches styled-components HostedBy */}
      {hostedBy && (
        <div className="text-lg leading-5 italic mb-4 text-gray-500">
          {hostedBy}
        </div>
      )}

      {/* AdditionalInfo - matches styled-components AdditionalInfo */}
      {iconBlocks && iconBlocks.length > 0 && (
        <div data-testid="additional-info" className="flex flex-col gap-6 py-4">
          {iconBlocks.map(
              (iconBlock, _index) => iconBlock && (
                <IconBlock
                  key={iconBlock.title}
                  iconType="fal"
                  icon={iconBlock.icon}
                  title={iconBlock.title}
                  subtitle={iconBlock.subtitle}
                  linkText={iconBlock.linkText}
                  linkRef={iconBlock.linkRef}
                  titleType={iconBlock.titleType}
                  shouldOpenInNewTab={iconBlock.shouldOpenInNewTab}
                />
              ),
          )}
        </div>
      )}

      {/* DescriptionWrapper - matches styled-components Description */}
      <div className={`font-normal text-md leading-5 sm:leading-4 ${!showRichDescription ? 'whitespace-break-spaces' : ''}`}>
        {descriptionTitleText && (
          <div data-testid="description-title" className="mb-3 leading-5">
            {descriptionTitleText}
          </div>
        )}
        {showRichDescription && richDescription ? (
          <div
            ref={richDescriptionRef}
            data-testid="rich-description"
            className="rich-description"
            dangerouslySetInnerHTML={{ __html: richDescription }}
          />
        ) : (
          <TextWithLinks text={description} viewDetailText={descriptionViewDetailText} onLinkClick={trackDescriptionLinkClick} />
        )}
        {participantsCount && (
          <div data-testid="quantity-notice" className="ml-4 sm:ml-6 mt-6 sm:mt-8 italic font-normal text-xs sm:text-sm leading-5 sm:leading-6 text-gray-600">
            {haveClaimedText}
          </div>
        )}
      </div>
    </div>
  );
};
